// src/edu-world/ui/dialogue-box.ts
// Dialogue box using a separate UI scene overlay for reliable button interaction.
// This avoids the Phaser scrollFactor + interactive object hit-test issue
// where camera-following scenes misalign click detection on fixed UI elements.

import Phaser from 'phaser';

export interface DialogueOptions {
  npcName: string;
  text: string;
  showMissionOptions?: boolean;
  onAccept?: () => void;
  onDecline?: () => void;
}

/**
 * UIOverlay scene: renders on top of everything with its own non-scrolling camera.
 * This ensures buttons receive clicks at the correct screen positions.
 */
export class UIOverlayScene extends Phaser.Scene {
  constructor() {
    super({ key: 'UIOverlay' });
  }

  create(): void {
    // This scene has no camera movement — all objects are screen-fixed by default.
  }
}

export class DialogueBox {
  private scene: Phaser.Scene; // The UI overlay scene
  private elements: Phaser.GameObjects.GameObject[] = [];
  private isVisible: boolean = false;
  private pendingOnAccept: (() => void) | undefined;
  private pendingOnDecline: (() => void) | undefined;

  constructor(scene: Phaser.Scene) {
    // We need the UI overlay scene. If it exists, use it. Otherwise use the passed scene.
    const uiScene = scene.scene.get('UIOverlay');
    this.scene = uiScene && uiScene.scene.isActive() ? uiScene : scene;
  }

  /** Show dialogue */
  show(options: DialogueOptions): void {
    this.destroyElements();
    this.isVisible = true;
    this.pendingOnAccept = options.onAccept;
    this.pendingOnDecline = options.onDecline;

    const w = this.scene.cameras.main.width;
    const h = this.scene.cameras.main.height;
    const cx = w / 2;
    const cy = h - 100;

    // ── Background panel ──
    const bg = this.scene.add.rectangle(cx, cy, 360, 160, 0xffffff, 0.95);
    bg.setStrokeStyle(3, 0x90caf9);
    bg.setDepth(100);
    this.elements.push(bg);

    // ── NPC name ──
    const nameText = this.scene.add.text(cx - 160, cy - 65, `🌸 ${options.npcName}`, {
      fontSize: '16px',
      fontFamily: 'sans-serif',
      color: '#c2185b',
      fontStyle: 'bold',
    });
    nameText.setDepth(101);
    this.elements.push(nameText);

    // ── Dialogue text ──
    const dialogueText = this.scene.add.text(cx - 160, cy - 38, options.text, {
      fontSize: '14px',
      fontFamily: 'sans-serif',
      color: '#333333',
      wordWrap: { width: 320 },
      lineSpacing: 6,
    });
    dialogueText.setDepth(101);
    this.elements.push(dialogueText);

    // ── Mission accept/decline buttons ──
    if (options.showMissionOptions) {
      this.createButtons(cx, cy);
    }
  }

  /** Hide dialogue */
  hide(): void {
    if (!this.isVisible) return;
    this.isVisible = false;
    this.pendingOnAccept = undefined;
    this.pendingOnDecline = undefined;
    this.destroyElements();
  }

  private createButtons(cx: number, cy: number): void {
    const btnY = cy + 45;

    // ── Accept button (large green circle with ✓) ──
    const acceptBg = this.scene.add.circle(cx - 55, btnY, 30, 0x4caf50);
    acceptBg.setStrokeStyle(3, 0x2e7d32);
    acceptBg.setDepth(102);
    acceptBg.setInteractive({ useHandCursor: true });
    this.elements.push(acceptBg);

    const acceptLabel = this.scene.add.text(cx - 55, btnY, '✓', {
      fontSize: '28px',
      color: '#ffffff',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    acceptLabel.setDepth(103);
    this.elements.push(acceptLabel);

    const acceptHint = this.scene.add.text(cx - 55, btnY + 36, 'Sim!', {
      fontSize: '11px',
      fontFamily: 'sans-serif',
      color: '#2e7d32',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    acceptHint.setDepth(101);
    this.elements.push(acceptHint);

    acceptBg.on('pointerdown', () => {
      const callback = this.pendingOnAccept;
      this.hide();
      callback?.();
    });

    // ── Decline button (orange circle with 👋) ──
    const declineBg = this.scene.add.circle(cx + 55, btnY, 30, 0xff9800);
    declineBg.setStrokeStyle(3, 0xe65100);
    declineBg.setDepth(102);
    declineBg.setInteractive({ useHandCursor: true });
    this.elements.push(declineBg);

    const declineLabel = this.scene.add.text(cx + 55, btnY, '👋', {
      fontSize: '22px',
    }).setOrigin(0.5);
    declineLabel.setDepth(103);
    this.elements.push(declineLabel);

    const declineHint = this.scene.add.text(cx + 55, btnY + 36, 'Não', {
      fontSize: '11px',
      fontFamily: 'sans-serif',
      color: '#e65100',
      fontStyle: 'bold',
    }).setOrigin(0.5);
    declineHint.setDepth(101);
    this.elements.push(declineHint);

    declineBg.on('pointerdown', () => {
      const callback = this.pendingOnDecline;
      this.hide();
      callback?.();
    });
  }

  private destroyElements(): void {
    for (const el of this.elements) {
      el.destroy();
    }
    this.elements = [];
  }

  getIsVisible(): boolean {
    return this.isVisible;
  }
}
